/**
 * @file Manifest wire format for chunked attachment versions (SYD-2378 R3):
 * the write-once JSON at `attachments/{fileUid}/manifests/{versionId}` naming,
 * in order, the content-addressed chunks a version assembles from. Consumers:
 * the version callables and the chunk sweep in `functions/src/workers/`, the
 * reassembling reader in `react/src/`, and the desktop sync agent in the
 * `file-sync` repo — a cross-repo contract keyed by `formatVersion`, which a
 * reader refuses when unknown rather than parsing best-effort. Invariants:
 * `chunks[].h` is the sha256 of the *uncompressed* chunk bytes and equals that
 * chunk's Storage path segment; `fileSize` equals the sum of `chunks[].s`; an
 * empty file is `fileSize: 0` with `chunks: []`.
 */

/**
 * Whether a version's bytes are one Storage object or a manifest of chunks. A
 * manual upload from the attachments panel is one object; a desktop sync save is
 * chunks, including its first sync, which uploads every chunk because it has
 * none to skip. Absent reads as `'object'`.
 */
export type AttachmentVersionLayout = 'object' | 'chunked';

/**
 * The chunker that produced a manifest. Informative only — a reader
 * reassembles from `chunks` alone and never runs a chunker — so this exists so
 * a future writer can compare dedupe efficiency across parameter sets. Two
 * manifests dedupe against each other only where their parameters match.
 */
export type AttachmentChunkerParameters = {
  /** Algorithm and revision, e.g. `fastcdc-v1`. */
  name: string;
  /** Smallest boundary the chunker will cut, in bytes. */
  minBytes: number;
  /** Target chunk size the chunker aims at, in bytes. */
  avgBytes: number;
  /** Largest chunk the chunker will emit, in bytes. */
  maxBytes: number;
};

/**
 * One chunk of a version, in file order. Offsets are prefix sums of `s` and
 * are never stored.
 */
export type AttachmentChunkEntry = {
  /**
   * 64-character lowercase hex sha256 of the uncompressed chunk bytes,
   * identical to the chunk's own Storage path segment at
   * `attachments/{fileUid}/chunks/{h}`.
   */
  h: string;
  /**
   * Uncompressed size in bytes: at least 1, at most
   * `MAX_ATTACHMENT_CHUNK_BYTES`.
   */
  s: number;
  /**
   * Absent means the stored object is the raw chunk bytes; `'zstd'` means it is
   * zstd-compressed. `h` and `s` always describe the uncompressed bytes, so
   * dedupe and verification are unaffected by this flag.
   */
  enc?: 'zstd';
};

/**
 * A version's chunk list plus the whole-file facts a reader verifies the
 * assembly against. Written once, never rewritten. Build one only through
 * `parseAttachmentChunkManifest`, which is where the refusal rules live.
 */
export type AttachmentChunkManifest = {
  /** Contract version. A reader refuses any value it does not know. */
  formatVersion: 1;
  /**
   * Optional because a reader never needs it: `parseAttachmentChunkManifest`
   * mirrors it when well-formed and drops it otherwise, so consumers handle
   * its absence rather than trust a fabricated default.
   */
  chunker?: AttachmentChunkerParameters;
  /** Chunk digest algorithm; `'sha256'` in v1. */
  hash: 'sha256';
  /** Uncompressed whole-file size; equals the sum of `chunks[].s`. */
  fileSize: number;
  /**
   * Base64 md5 of the whole uncompressed file, writer-computed — Cloud Storage
   * has no whole object to hash. Carries the same value as the version
   * document's `md5Hash`; the two names differ because this one is fixed by the
   * cross-repo contract. Always exactly 24 characters, the base64 of a 16-byte
   * digest: `parseAttachmentChunkManifest` refuses anything else, because
   * nothing in this repo reads the value and a placeholder would surface only in
   * `file-sync`, as every download of that version failing forever.
   */
  contentMd5: string;
  /** Ordered. Empty exactly when `fileSize` is 0. */
  chunks: AttachmentChunkEntry[];
};

/** The only manifest contract version this repo reads or writes. */
export const ATTACHMENT_MANIFEST_FORMAT_VERSION = 1;

/** Ceiling on `chunks[].s`: 8 MiB, the chunker's maximum emitted chunk. */
export const MAX_ATTACHMENT_CHUNK_BYTES = 8 * 1024 * 1024;

/**
 * Ceiling `storage.rules` puts on a stored chunk object: twice
 * `MAX_ATTACHMENT_CHUNK_BYTES`, because a `zstd` chunk can in principle exceed
 * its plaintext size and the rules cannot decompress to check.
 */
export const MAX_STORED_ATTACHMENT_CHUNK_BYTES = 16 * 1024 * 1024;

/**
 * Ceiling `storage.rules` puts on a stored manifest object: 1 MiB, roughly ten
 * times what a 1 GiB file needs at the default chunker parameters.
 */
export const MAX_ATTACHMENT_MANIFEST_BYTES = 1024 * 1024;

/**
 * Exclusive upper bound on one attachment version's whole file, 5 GiB. For an
 * `object` version it mirrors the `< 5 GiB` of `storage.rules`, so a reservation
 * can never license an upload the rules then refuse. For a `chunked` version no
 * single object approaches it — it is a declared whole-file bound, taken from
 * `sizeBytes` at `beginAttachmentVersion` and re-checked against the manifest's
 * `fileSize` at `commitAttachmentVersion`, which is the only place the whole
 * file exists as a number. Both callables read it from here so the two lanes
 * cannot drift apart.
 */
export const MAX_ATTACHMENT_FILE_BYTES = 5 * 1024 * 1024 * 1024;

/**
 * Size above which the web client considers a browser upload a poor idea and
 * would rather the desktop sync agent carried the file, 500 MiB. Advisory only:
 * nothing refuses an upload because of it, unlike `MAX_ATTACHMENT_FILE_BYTES`,
 * which is a hard cap the storage rules and the version callables enforce.
 */
export const LARGE_ATTACHMENT_ADVISORY_BYTES = 500 * 1024 * 1024;
